/** @jsxImportSource @emotion/react */
"use client";

import { useTranslations } from "@i18n/react";
import { Link } from "@packages/framework";
import { isDefaultPlan, useRequestProducts } from "@packages/sdk";
import { type MouseEventHandler } from "react";

import { numericPixels, spacing } from "../../../../../../global/emotion";
import { Button } from "../../../../../components/_base/_core/Button/Button.emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";
import type { ContentMarqueeProps } from "../../../../../components/blocks/other/ContentMarquee/ContentMarquee.emotion";
import { ContentMarquee } from "../../../../../components/blocks/other/ContentMarquee/ContentMarquee.emotion";
import { emotionDynamics } from "../../../../../lib/emotionDynamics";

const marqueeStyle: Interpolation = {
  marginBottom: spacing.l,
};
const subscriptionContainerStyle: Interpolation = {
  placeSelf: "center",
  width: numericPixels[320],
};
const textContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  justifyContent: "flex-start",
  marginBottom: spacing.xl,
  textAlign: "center",
};

import type { Interpolation } from "@emotion/react";

import { useGetNumberFormattedFn } from "../../../../../hooks/useGetNumberFormattedFn";

const MARQUEE_IMAGES: ContentMarqueeProps["content"] = [
  {
    titleMessage: "",
    colorHex: "transparent",
    image: "https://images.hallow.app/covers/16_l.webp",
  },
  {
    titleMessage: "",
    colorHex: "transparent",
    image: "https://images.hallow.app/covers/662_l.webp",
  },
  {
    titleMessage: "",
    colorHex: "transparent",
    image: "https://images.hallow.app/covers/1744_l.webp",
  },
  {
    titleMessage: "",
    colorHex: "transparent",
    image: "https://images.hallow.app/covers/381_l.webp",
  },
  {
    titleMessage: "",
    colorHex: "transparent",
    image: "https://images.hallow.app/covers/741_l.webp",
  },
  {
    titleMessage: "",
    colorHex: "transparent",
    image: "https://images.hallow.app/covers/1197_l.webp",
  },
];

export const PostPrayerSubscriptionBody = () => {
  const t = useTranslations();
  return (
    <div css={subscriptionContainerStyle}>
      <ContentMarquee
        content={MARQUEE_IMAGES}
        maskImages={false}
        css={[
          marqueeStyle,
          emotionDynamics.mask({
            directions: ["to right", "to left"],
            spreads: [32, 32], // spacingMasks.xs
          }),
        ]}
        size={"s"}
        speed={10}
      />
      <div css={textContainerStyle}>
        <Text as={"h2"} type={"title"} size={"xl"}>
          {t("web_post_prayer_subscribe_title")}
        </Text>
        <Text as={"p"} type={"body"} size={"l"}>
          {t("sub_screen_subtitle_unlock_all")}
        </Text>
      </div>
    </div>
  );
};

export const PostPrayerSubscriptionButton = ({
  onClick,
}: {
  onClick?: MouseEventHandler<any>;
}) => {
  const { query: products } = useRequestProducts();
  const t = useTranslations();
  const { getNumberFormattedFn } = useGetNumberFormattedFn();

  const getNumberFormatted = getNumberFormattedFn({
    currency: products?.data?.[0]?.price?.currency,
  });

  const trial = products.data?.find((plan) => isDefaultPlan(plan))?.trial_days;
  const str = !!trial
    ? t("redeem_trial_zero_dollars", {
        "0": trial,
        "1": getNumberFormatted.format(0),
      })
    : t("general_phrase_subscribe_to_unlock");
  return (
    <Button asChild onClick={onClick}>
      <Link href={"/settings/subscription"}>{str}</Link>
    </Button>
  );
};
